/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { apiClient } from './client.js';

import type { OutstandingInvoice } from './finance.js';

export interface FinanceDashboardStats {
  readonly revenuePaise: number;
  readonly revenueTrendPct: number | null;
  readonly outstandingPaise: number;
  readonly processedTodayPaise: number;
  readonly netProfitPaise: number;
  readonly netProfitTrendPct: number | null;
}

/** The four Figma dashboard tiles + proforma pipeline (server-computed). */
export interface FinanceFigmaStats {
  readonly invoicedMonthPaise: number;
  readonly collectedMonthPaise: number;
  readonly overduePaise: number;
  readonly overdueCount: number;
  readonly proformasDraft: number;
  readonly proformasSent: number;
  readonly proformasApproved: number;
}

export interface RevenueExpensePoint {
  readonly monthIso: string;
  readonly revenuePaise: number;
  readonly expensesPaise: number;
}

export interface PaymentMethodSlice {
  readonly method: string;
  readonly amountPaise: number;
}

export interface RecentPayment {
  readonly id: string;
  readonly clientName: string;
  readonly method: string;
  readonly amountPaise: number;
  readonly paidIso: string;
}

export interface PayrollSummaryRow {
  readonly therapistName: string;
  readonly sessions: number;
  readonly amountPaise: number;
}

export interface PayrollSummary {
  readonly periodLabel: string;
  readonly status: string;
  readonly rows: readonly PayrollSummaryRow[];
}

export interface FinanceDashboard {
  readonly stats: FinanceDashboardStats;
  readonly figma: FinanceFigmaStats;
  readonly revenueExpenses: readonly RevenueExpensePoint[];
  readonly paymentMethods: readonly PaymentMethodSlice[];
  readonly outstanding: readonly OutstandingInvoice[];
  readonly recentPayments: readonly RecentPayment[];
  readonly payroll: PayrollSummary | null;
}

export const financeDashboardApi = {
  async overview(): Promise<FinanceDashboard> {
    const { data } = await apiClient.get<FinanceDashboard>('/finance/dashboard');
    return data;
  },
};
